import { Field, FieldInputProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAppToasts, { useShowError } from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { toTitle } from "@/utils/case";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { ForgotPasswordFormValues } from "./forgot-password-page";
import { checkPasswordComplexity } from "../utils/password-utils";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import { isMobile } from "react-device-detect";
import AppButton from "@/features/line-sheet-sets/app-button";
import useChangePasswordWithoutAuth from "@/features/auth/hooks/use-change-password-withous-auth";
import { authApi } from "@/features/auth/hooks/use-get-user";

export interface ForgotPasswordStep2FormFields {
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface ForgotPasswordStep2FormProps {
  initialValue: ForgotPasswordFormValues;
}

export default function ForgotPasswordStep2Form({
  initialValue,
}: ForgotPasswordStep2FormProps) {
  const tempToken = initialValue.tempToken;
  const [userList, setUserList] = useState<{ email: string }[]>([]);

  useEffect(() => {
    const fetchUserList = async () => {
      try {
        const response = await authApi.getUserList(tempToken);
        if (response.data) {
          setUserList(response.data.users);
        }
      } catch (error) {
        console.error("Error fetching user list:", error);
      }
    };

    // ✅ 함수 호출!
    fetchUserList();
  }, [tempToken]);

  const { t, tTitle, tSentence } = useI18nHelper();
  const { error: showError, info: showInfo } = useAppToasts();
  const { navigate } = useRouterHelper();
  const { withMessage, formT } = useYupHelper({
    prefix: "forgot_password_form",
    ns: "auth",
  });
  const validateSchema = useMemo(() => {
    return Yup.object({
      email: Yup.string()
        .email(withMessage.email),
        // .required(withMessage.required),
      password: Yup.string()
        .test(
          "PasswordDiversity",
          tSentence("auth:sign_up_form.messages.error.not_diverse_password"),
          (value, context) => {
            return checkPasswordComplexity(value);
          }
        )
        .required(withMessage.required),
      passwordConfirmation: Yup.string().test(
        "PasswordEquality",
        tSentence("auth:sign_up_form.messages.error.not_identical_password"),
        (value, context) => {
          const { password } = context.parent;
          if (password && password.length >= 8) {
            return value === context.parent.password;
          }
          return true;
        }
      ),
    });
  }, [tSentence, withMessage.email, withMessage.required]);

  const {
    isLoading: isChangePasswordLoading,
    data: getChangePasswordData,
    error: getChangePasswordError,
    fire: fireChangePassword,
  } = useChangePasswordWithoutAuth();

  useShowError(getChangePasswordError, (e) => showError(e, {
    id: "CHANGE_PASSWORD_ERROR",
    i18n: {
      keyPrefix: "forgot_password.messages.change_password.error",
      ns: "auth",
    },
  }));

  const showSuccessAndNavigate = useCallback(() => {
    showInfo(t("auth:forgot_password.messages.change_password.success"));
    setTimeout(() => {
      navigate("/auth/sign-in", { replace: true });
    }, 3000);
  }, [showInfo, t, navigate]);

  const [isChangePasswordSuccess, setIsChangePasswordSuccess] = useState(false);

  useEffect(() => {
    if (getChangePasswordData && getChangePasswordData.message === "success") {
      setIsChangePasswordSuccess(true);
      showSuccessAndNavigate();
    }
  }, [getChangePasswordData, showInfo, t, showSuccessAndNavigate]);

  return (
    <Formik
      initialValues={initialValue as ForgotPasswordStep2FormFields}
      validationSchema={validateSchema}
      onSubmit={async (values, actions) => {
        await fireChangePassword(
          {},
          {
            tempToken: initialValue.tempToken,
            email: userList.length === 1 ? userList[0].email : values.email,
            password: values.password,
          }
        );
        actions.setSubmitting(true);
      }}
    >
      {(props: FormikProps<ForgotPasswordStep2FormFields>) => {
        if (getChangePasswordError && props.isSubmitting) {
          props.setSubmitting(false);
        }
        return (
          <Form className="flex flex-col gap-2">
            <Box display="flex" flexDirection="column" gap="32px">
              <Box display="flex" flexDirection="column" gap="16px">
                <Field name="email">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<string>;
                    form: FormikProps<ForgotPasswordStep2FormFields>;
                  }) => (
                    <FormControl
                      isRequired
                      isInvalid={!!form.errors.email && form.touched.email}
                    >
                      <FormLabel display="flex" m={"0"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle("email")}
                        </Text>
                      </FormLabel>
                      {userList.length === 1 ? (
                        // ✅ 값 1개일 때: 그냥 텍스트로 표시
                        <Input
                          {...field}
                          type={"text"}
                          value={userList[0].email}
                          readOnly
                          style={{
                            // globals.css의 모바일 16px 규칙은 인라인 style을 이기지
                            // 못한다. iOS 자동 확대를 막으려면 여기서 직접 올려야 한다.
                            fontSize: isMobile ? "16px" : "14px",
                            padding: "8px 12px",
                            backgroundColor: "#e9ecef",
                            borderRadius: "6px",
                            border: "1px solid #ced4da",
                          }}
                        />
                      ) : ( <select
                        id="user-select"
                        value={field.value}
                        onChange={(e) => {
                          form.setFieldValue("email", e.target.value);
                        }}
                        style={{
                          height: "40px",
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #ced4da",
                          borderRadius: "6px",
                          // globals.css의 모바일 16px 규칙은 인라인 style을 이기지
                          // 못한다. iOS 자동 확대를 막으려면 여기서 직접 올려야 한다.
                          fontSize: isMobile ? "16px" : "14px",
                          color: "#212529",
                          backgroundColor: "#fff",
                          outline: "none",

                          // ✅ 기본 화살표 제거
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",

                          // ✅ custom arrow 아이콘 (배경에 삽입)
                          backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='gray' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 10px center",
                          backgroundSize: "16px 16px",
                        }}
                      >
                        <option value="">-- 이메일 선택 --</option>
                        {userList.map((user, idx) => (
                          <option key={idx} value={user.email}>
                            {user.email}
                          </option>
                        ))}
                      </select> )}
                      <Flex h="24px" flexDirection="row" alignItems={"center"}>
                        <FormErrorMessage m="0" fontSize={"12px"}>
                          {form.errors.email}
                        </FormErrorMessage>
                      </Flex>
                    </FormControl>
                  )}
                </Field>
                <Field name="password">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<string>;
                    form: FormikProps<ForgotPasswordStep2FormFields>;
                  }) => (
                    <FormControl
                      isRequired
                      isInvalid={
                        !!form.errors.password && form.touched.password
                      }
                    >
                      <FormLabel display="flex" m={"0"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle("password")}
                        </Text>
                      </FormLabel>
                      <Input
                        {...field}
                        type={"password"}
                        placeholder={tSentence(
                          "auth:sign_up_form.field.password.placeholder"
                        )}
                      />
                      <Flex
                        minH="24px"
                        flexDirection="row"
                        alignItems={"center"}
                      >
                        <FormErrorMessage m="0" fontSize={"12px"}>
                          {form.errors.password}
                        </FormErrorMessage>
                      </Flex>
                    </FormControl>
                  )}
                </Field>
                <Field name="passwordConfirmation">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<string>;
                    form: FormikProps<ForgotPasswordStep2FormFields>;
                  }) => (
                    <>
                      <FormControl
                        isRequired
                        isInvalid={
                          !!form.errors.passwordConfirmation &&
                          form.touched.passwordConfirmation
                        }
                      >
                        <FormLabel display="flex" m={"0"}>
                          <Text
                            color="#6F6F6F"
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            {tTitle("password_confirmation")}
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          type={"password"}
                          placeholder={tSentence(
                            "auth:sign_up_form.field.password_confirmation.placeholder"
                          )}
                        />
                        <Flex
                          h="24px"
                          flexDirection="row"
                          alignItems={"center"}
                        >
                          <FormErrorMessage m="0" fontSize={"12px"}>
                            {form.errors.passwordConfirmation}
                          </FormErrorMessage>
                        </Flex>
                      </FormControl>
                    </>
                  )}
                </Field>
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                gap="20px"
                className={"flex flex-col gap-5"}
              >
                <AppButton
                  variant={"gray"}
                  size={"large"}
                  width={"100%"}
                  chakraProps={{ height: "40px" }}
                  isLoading={isChangePasswordLoading}
                  isDisabled={!props.isValid || isChangePasswordSuccess}
                  type={"submit"}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await props.submitForm();
                  }}
                >
                  <Text fontSize={"14px"}>
                    {tTitle("auth:forgot_password.button.change_password.do")}
                  </Text>
                </AppButton>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
